/**
 * ShareConfirmListener — mounted once at the App root.
 *
 * Watches for `shareLinkedInPending` in localStorage. When the tab regains
 * focus (or visibility) and the flag is < 10 minutes old, shows a custom
 * toast asking "Did your LinkedIn post go live?". User answers
 * Yes / Skip → POSTed to /api/share-events with posted=true|false.
 *
 * Renders nothing visible until triggered. Fully self-contained.
 */
import { useEffect, useRef, useState } from 'react';
import { Linkedin, Check, X } from 'lucide-react';
import { SHARE_PENDING_KEY } from '../hooks/useCardShare';

const API = process.env.REACT_APP_BACKEND_URL;
const PENDING_TTL_MS = 10 * 60 * 1000;        // 10 min — past this we silently drop
const MIN_AWAY_MS = 4 * 1000;                 // user needs to be away ≥4s before we ask

function fireEvent(target, posted) {
  try {
    fetch(`${API}/api/share-events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ target, channel: 'linkedin', posted }),
      keepalive: true,
    }).catch(() => {});
  } catch {}
}

export default function ShareConfirmListener() {
  const [pending, setPending] = useState(null);  // {target, ts}
  const awaySince = useRef(null);

  useEffect(() => {
    const onHidden = () => {
      if (document.visibilityState === 'hidden') {
        awaySince.current = Date.now();
      }
    };
    const onReturn = () => {
      // Only run on actual visible/focus — hidden events shouldn't clear awaySince.
      if (document.visibilityState !== 'visible') return;

      // Only reveal if we'd been hidden long enough — avoids firing on
      // simple mouse-tab switches without user actually visiting LinkedIn.
      const wasAway = awaySince.current ? (Date.now() - awaySince.current) : 0;
      awaySince.current = null;
      if (wasAway < MIN_AWAY_MS) return;

      let raw;
      try { raw = localStorage.getItem(SHARE_PENDING_KEY); } catch { return; }
      if (!raw) return;

      let payload;
      try { payload = JSON.parse(raw); } catch { return; }
      if (!payload || !payload.target || !payload.ts) {
        try { localStorage.removeItem(SHARE_PENDING_KEY); } catch {}
        return;
      }
      if (Date.now() - payload.ts > PENDING_TTL_MS) {
        try { localStorage.removeItem(SHARE_PENDING_KEY); } catch {}
        return;
      }
      setPending(payload);
    };

    document.addEventListener('visibilitychange', onHidden);
    document.addEventListener('visibilitychange', onReturn);
    window.addEventListener('focus', onReturn);
    return () => {
      document.removeEventListener('visibilitychange', onHidden);
      document.removeEventListener('visibilitychange', onReturn);
      window.removeEventListener('focus', onReturn);
    };
  }, []);

  const dismiss = () => {
    try { localStorage.removeItem(SHARE_PENDING_KEY); } catch {}
    setPending(null);
  };

  const respond = (posted) => {
    if (pending?.target) fireEvent(pending.target, posted);
    dismiss();
  };

  if (!pending) return null;

  const targetLabel = pending.target === 'borough'
    ? 'the Borough Reality Card'
    : `the ${pending.target} card`;

  return (
    <div
      className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[9998] w-[340px] max-w-[92vw] rounded-[8px] bg-[#090909] text-white shadow-[0_12px_32px_-6px_rgba(0,0,0,0.45)] border border-white/10 overflow-hidden animate-fade-in"
      data-testid="share-confirm-toast"
      role="dialog"
      aria-live="polite"
    >
      {/* Top accent bar */}
      <div className="h-[3px] bg-gradient-to-r from-[#0A66C2] via-[#FE4A2A] to-[#2D6E4E]" />
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-[#0A66C2]/15 flex items-center justify-center shrink-0">
            <Linkedin className="w-4 h-4 text-[#0A66C2]" strokeWidth={2} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-display text-[12px] font-extrabold uppercase tracking-wider text-[#0A66C2]">
              Posted on LinkedIn?
            </p>
            <p className="font-body text-[12.5px] text-white/85 mt-1 leading-snug">
              Did your post with {targetLabel} go live? Help us track viral reach for the next funding pitch.
            </p>
          </div>
          <button
            onClick={dismiss}
            aria-label="Close"
            className="text-white/40 hover:text-white shrink-0"
            data-testid="share-confirm-toast-close"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="flex gap-2 mt-3">
          <button
            onClick={() => respond(true)}
            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-[5px] bg-[#2D6E4E] hover:bg-[#2D6E4E]/85 transition-colors font-body text-[12px] font-medium text-white"
            data-testid="share-confirm-toast-yes"
          >
            <Check className="w-3.5 h-3.5" strokeWidth={2.5} />
            Yes, it's live
          </button>
          <button
            onClick={() => respond(false)}
            className="px-3 py-2 rounded-[5px] bg-white/[0.06] hover:bg-white/[0.12] border border-white/10 transition-colors font-body text-[12px] text-white/70 hover:text-white"
            data-testid="share-confirm-toast-skip"
          >
            Skip
          </button>
        </div>
      </div>
    </div>
  );
}
